<template>
  <div>
    <o-working-overlay
      :title="'Allocating...'"
      :working="working"
    ></o-working-overlay>

    <b-navbar variant="light">
      <b-collapse id="nav-collapse" is-nav>
        <b-navbar-nav>
          <strong @click="enableDebug($event)">Job Allocation</strong>
        </b-navbar-nav>
      </b-collapse>

      <b-collapse id="nav-collapse" is-nav>
        <b-navbar-nav>
          <b-nav-form>
            <o-date-time
              v-model="filters.lhs_selected_date"
              :form-state="formState"
              :hide-time="hideTime"
              :nolabel="nolabel"
              label="When"
              name="lhs_selected_date"
              placeholder="Date of Tasks"
              @input="loadDataForDate()"
            ></o-date-time>
            <button class="btn btn-default" @click="setToday">Today</button>
          </b-nav-form>
        </b-navbar-nav>
        <b-navbar-nav>
          <b-form-select
            v-model="selected_task_type"
            :form-state="formState"
            :loading="optionCodeJobTaskTypeWorking"
            :options="optionCodeJobTaskTypeItems"
            name="task_type"
            placeholder="Task Type"
            text-field="code"
            value-field="id"
            @input="changeTaskType"
          >
          </b-form-select>
        </b-navbar-nav>

        <b-navbar-nav class="ml-auto">
          <b-button
            v-if="listChanged"
            class="ml-0"
            variant="info"
            @click="save('patch')"
          >
            Save
          </b-button>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>

    <table
      class="table table-bordered table-hover table-condensed smaller-table"
    >
      <thead>
      <tr>
        <th>PO Number</th>
        <th>Customer</th>
        <th>Site Address</th>
        <th>Area</th>
        <th>Delivery Time</th>

        <th v-for="product in product_list" class="qty_cell">
          {{ product.description }}
        </th>

        <th style="width: 300px;">Driver</th>
        <th>Start Time</th>
        <th>Actions</th>
      </tr>
      </thead>
      <draggable v-model="jobs_list" handle=".handle" tag="tbody" @end="finishedDrop">
        <tr v-for="(row, index) in jobs_list" :key="get_key(row)">
          <td scope="row">{{ row.po_number }}</td>
          <td>
            <lookup-display
              :list="optionCustomerItemsById"
              :value="row.customer"
              default-value="-"
              display-field="name"
            ></lookup-display>
          </td>
          <td>
            <router-link :to="{ name: 'job.edit', params: { id: row.id } }">{{
                row.delivery_address || "-"
              }}
            </router-link>
          </td>
          <td>
            <lookup-display
              :list="optionCodeAreaItemsById"
              :value="row.area"
              default-value="-"
              display-field="description"
            ></lookup-display>
          </td>
          <td>
            <span v-if="selected_task_type == 1">
              <lookup-display
                :list="optionCodeTimeOfDayItemsById"
                :value="row.requested_delivery_date_time_of_day"
                default-value="-"
                display-field="description"
              ></lookup-display>
            </span>
            <span v-if="selected_task_type == 2">
              <lookup-display
                :list="optionCodeTimeOfDayItemsById"
                :value="row.requested_scrap_pickup_time_of_day"
                default-value="-"
                display-field="description"
              ></lookup-display>
            </span>
          </td>
          <td v-for="product in product_list" class="qty_cell">
            {{ getQuantity(row, product.id) }}
          </td>
          <td>
            <o-tags
              v-model="row.truck_list"
              :form-state="formState"
              :items="optionCodeTruckItems"
              :loading="optionCodeTruckWorking"
              :nolabel="nolabel"
              :server-errors="row.serverErrors"
              class="bg-transparent"
              text-field="name"
              v-bind="taskSchema.fields.truck"
              value-field="pk"
              @input="rowChanged($event, row)"
            ></o-tags>
          </td>
          <td class="text-center">
            <o-text-field
              v-model="row.start_time"
              :form-state="formState"
              :nolabel="nolabel"
              :server-errors="row.serverErrors"
              class="bg-transparent"
              name="start_time"
              size="sm"
              v-bind="taskSchema.fields.start_time"
              @input="rowChanged($event, row)"
            ></o-text-field>
          </td>
          <td>
            <div class="list-actions">
              <o-list-actions
                v-model="jobs_list"
                :disable-delete="disableDelete"
                :disable-move="disableMove"
                :display-order="displayOrder"
                :index="index"
                @change="save('day_order')"
              >
                <template v-slot:suffix>
                  <b-button class="handle" size="sm">
                    <drag-horizontal-icon></drag-horizontal-icon>
                  </b-button>
                </template>
              </o-list-actions>
            </div>
          </td>
        </tr>
      </draggable>
    </table>
  </div>
</template>

<style>
.disabled {
  background: silver;
}
</style>

<script>
import _ from "lodash";
import * as moment from "moment";
import {CreateCodeTableFetchMixin, UseRestModuleMixin,} from "../../../vue-ocom/mixins";
import LookupDisplay from "../../../vue-ocom/components/display/LookupDisplay";
import ODoServerActionButton from "../../../vue-ocom/components/forms/ODoServerActionButton";
import schema from "../../schema";
import {cleanUpModel} from "../../../vue-ocom/schema/schema_vuetify";
import draggable from "vuedraggable";
import DragHorizontalIcon from "vue-material-design-icons/DragHorizontal";

export default {
  mixins: [
    CreateCodeTableFetchMixin([
      "CodeTruck",
      "CodeProduct",
      "CodeJobTaskType",
      "Customer",
      "CodeTimeOfDay",
      "CodeArea",
    ]),
    UseRestModuleMixin(["Job", "JobTask", "JobAllocation"]),
  ],
  components: {LookupDisplay, ODoServerActionButton, draggable, DragHorizontalIcon},
  data: function () {
    var today = moment().startOf("day")

    let last_date = sessionStorage.getItem("ip_allocation_date");
    let last_task_type = sessionStorage.getItem("ip_allocation_task_type") || 1;

    if (last_date) {
      today = moment(last_date);

      if (!today.isValid()) {
        today = moment().startOf("day")
      }
    }

    today = today.toISOString(true).split("T")[0]; // Just the Date part for Today Local

    return {
      failedPatch: false,
      listChanged: false,
      debug: false,
      taskSchema: cleanUpModel(schema.JobTask, {}),
      jobs_list: [],
      filters: {lhs_selected_date: today},
      selected_task_type: last_task_type,

      clearable: true,
      disableDelete: true,
      disableMove: false,
      displayOrder: "day_order",

      formState: {},
      hideTime: true,
      nolabel: true,

      dragging: false,
    };
  },
  methods: {
    setToday() {
      var today = moment().startOf("day").toISOString(true).split("T")[0]; // Just the Date part for Today Local
      this.filters.lhs_selected_date = today;
      this.loadDataForDate()
    },
    get_key(row) {
      return row.id + ":" + row.po_number;
    },
    enableDebug(e) {
      if (e.shiftKey) {
        this.debug = !this.debug;
      }
    },
    changeTaskType(val) {
      this.selected_task_type = val;
      this.loadDataForDate();
    },
    rowChanged(event, item) {
      console.log(event);
      this.$set(item, "_changed", true);
      this.listChanged = true;
    },
    loadDataForDate() {
      //TODO remove query for tasks as it is merged in job_allocation query
      var vm = this;
      var task_type_search_field = "requested_delivery_date"; //default
      var task_type = this.optionCodeJobTaskTypeItemsById[
        this.selected_task_type
        ];
      if (task_type) {
        task_type_search_field = task_type.search_field;
      }

      var currentDateTime = moment(this.filters.lhs_selected_date).startOf('day').toISOString(true);
      sessionStorage.setItem(
        "ip_allocation_date",
        currentDateTime
      );
      sessionStorage.setItem(
        "ip_allocation_task_type",
        this.selected_task_type
      );

      // Get the Jobs (with tasks) for the Date
      this.getAllJobAllocation({
        [task_type_search_field]: this.filters.lhs_selected_date,
      }).then(function (result) {
        vm.jobs_list = result.items;
      });
    },
    finishedDrop(e) {
      var vm = this;
      // Change the Order
      _.forEach(vm.jobs_list, function (item, index) {
        if (item.day_order !== index) {
          item.day_order = index;
          item._changed = true;
          vm.listChanged = true;
        }
      });

    },
    save(action = null) {
      var vm = this;

      vm.failedPatch = false;

      var all_promises = _.map(vm.jobs_list, function (item) {
        if (item._changed || action === "day_order") {
          var when = moment(vm.filters.lhs_selected_date).toISOString(true).split('T')[0];
          var patch_data = {
            when: when,
            task_type: vm.selected_task_type,

            job_id: item.id,
            truck_list: item.truck_list,
            start_time: item.start_time,
            day_order: item.day_order,
            action: action,
          };

          return vm
            .patchJobAllocationItem(patch_data)
            .then((result) => {
              console.log(result);
            })
            .catch(function ({response}) {
              console.log(response);
              vm.failedPatch = true;
              if (response && response.data) {
                vm.$set(item, "serverErrors", response.data);
              }
            });
        }
      });

      Promise.all(all_promises).then(function () {
        if (!vm.failedPatch) {
          vm.loadDataForDate();
          vm.listChanged = false;
          vm.failedPatch = false;
        } else {
          alert("A row failed to update!");
        }
      });
    },
    getQuantity(row, product_id) {
      var qty = null;

      // total Quantity for product - incase there are more than 1 entries for the same product some how..
      _.each(row.products, function (prod) {
        if (prod.product == product_id) {
          qty = (qty || 0) + prod.ordered_quantity;
        }
      });

      if (qty != null) {
        return qty;
      } else {
        return "";
      }
    },
    row_class(row) {
      if (row._changed) {
        return "changed";
      }
      if (row.truck_list && row.truck_list.length) {
        return "allocated";
      }

      return "";
    },
  },
  computed: {
    working() {
      return (
        this.optionCodeTruckWorking ||
        this.optionJobTaskAllocationWorking ||
        this.optionJobTaskWorking ||
        this.optionCodeTimeOfDayWorking
      );
    },
    product_list() {
      return _.orderBy(this.optionCodeProductItems, "display_order");
    },
    create_upstream_request() {
      return {date: this.filters};
    },
  },
  created() {
    this.loadDataForDate();
  },
};
</script>
<style scoped>
tr.changed {
  background-color: #fffff7;
}

tr.allocated {
  background-color: #fbfffb;
}

.debug {
  font-size: 6pt;
}

thead {
  background-color: silver;
}

table.smaller-table {
  font-size: 8pt;
}

table td {
  font-size: 11pt;
}

.qty_cell {
  width: 4em;
  padding-left: 3px;
  padding-right: 3px;
  text-align: right;
}

table td {
  vertical-align: middle;
}

.table-bordered td {
  padding-top: 1px;
  padding-bottom: 1px;
}

.job-details td {
  border-width: 0;
  padding: 0;
}

.collapsing {
  -webkit-transition: none;
  transition: none !important;
  display: none;
}

.o_do_server_action_button {
  margin-left: 30px;
}
</style>
